import { useRef } from 'react'
import Draggable from 'react-draggable'
import type { DraggableEventHandler } from 'react-draggable'
import type { RefObject } from 'react'
import type { BubbleBounds } from './computeBubbleBounds'
import type { BubbleOffset } from './useChatBubbleOffset'
import { FloatingActionButton } from '@ztechapps/webui.util'
import { useTrackEvent } from '@ztechapps/webui.telemetry'
import { useFloatingChatBubbleContext } from './Context'
import styles from './FloatingChatBubble.module.scss'

export enum AiChatBubblePositionEnum {
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
}

// A press that travels this far on either axis is a drag, not a click.
const CLICK_SLOP_PX = 5

export type FloatingChatBubbleProps = {
  /** Additional CSS classes */
  className?: string
  /** Corner to anchor the FAB. Defaults to `BottomRight`. */
  position?: AiChatBubblePositionEnum
  /** Draggable bubble handle owned by `AiChatBubbleInternal`. */
  bubbleRef: RefObject<HTMLButtonElement>
  /** Admin opt-in: drag the bubble anywhere in the viewport. */
  isDraggable?: boolean
  /** Saved drag translation from the default corner. */
  offset?: BubbleOffset
  /** Translate limits keeping the whole bubble inside the viewport. */
  bounds?: BubbleBounds
  /** Persists the offset when a drag ends. */
  onOffsetSave?: (offset: BubbleOffset) => void
}

export const FloatingChatBubble = ({
  className = '',
  position = AiChatBubblePositionEnum.BottomRight,
  bubbleRef,
  isDraggable = false,
  offset = { x: 0, y: 0 },
  bounds,
  onOffsetSave,
}: FloatingChatBubbleProps) => {
  const { isOpen, toggleOpen, close } = useFloatingChatBubbleContext()
  const trackEvent = useTrackEvent()

  const size = 'sm'

  const dragStartRef = useRef<BubbleOffset>({ x: 0, y: 0 })
  const hasMovedRef = useRef(false)
  const suppressClickRef = useRef(false)

  const handleClick = () => {
    // The mouseup closing a drag lands on the button as a click — swallow it
    // so the window stays closed. This path also serves Enter/Space through
    // FloatingActionButton's handleKeyDown.
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    const action = isOpen ? 'close' : 'open'
    trackEvent('Clicked AI Button', { action, position })
    toggleOpen()
  }

  const handleStart: DraggableEventHandler = (_event, { x, y }) => {
    dragStartRef.current = { x, y }
    hasMovedRef.current = false
    suppressClickRef.current = false
  }

  const handleDrag: DraggableEventHandler = (_event, { x, y }) => {
    // A real drag closes the open window once, at the moment the pointer
    // leaves click range — the trailing mouseup is swallowed above.
    if (hasMovedRef.current) return
    const { x: startX, y: startY } = dragStartRef.current
    if (Math.abs(x - startX) > CLICK_SLOP_PX || Math.abs(y - startY) > CLICK_SLOP_PX) {
      hasMovedRef.current = true
      close()
    }
  }

  const handleStop: DraggableEventHandler = (event, { x, y }) => {
    // react-draggable cancels touchstart, so a tap fires no click — toggle here.
    if (!hasMovedRef.current) {
      if (event.type === 'touchend') toggleOpen()
      return
    }
    // The single storage write per drag.
    onOffsetSave?.({ x, y })
    suppressClickRef.current = event.type === 'mouseup' && bubbleRef.current?.contains(event.target as Node) === true
  }

  const buttonClasses = [className, isDraggable ? styles.draggableBubble : ''].filter(Boolean).join(' ')

  const button = (
    <FloatingActionButton
      ref={bubbleRef}
      icon="robot"
      onClick={handleClick}
      ariaLabel="Open AI Assistant"
      ariaExpanded={isOpen}
      position={position}
      size={size}
      className={buttonClasses}
      dataTestId="ai-chat-fab"
    />
  )

  if (!isDraggable) return button

  return (
    <Draggable
      nodeRef={bubbleRef}
      position={offset}
      bounds={bounds}
      onStart={handleStart}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      {button}
    </Draggable>
  )
}
